import path from 'path';
import fs from 'fs-extra';
import chalk from 'chalk';
import inquirer from 'inquirer';
import { getTemplateRegistry } from '../utils/registry.js';
import { copyTemplate } from '../utils/template.js';
import { updatePackageJson } from '../utils/package.js';

interface AddOptions {
  dir: string;
  force: boolean;
  yes: boolean;
}

export async function addComponents(type: string, options: AddOptions) {
  const validTypes = ['react-vanilla', 'react-tailwind', 'react-shadcn'];
  
  if (!validTypes.includes(type)) {
    throw new Error(`Invalid component type: ${type}. Valid types: ${validTypes.join(', ')}`);
  }

  const targetDir = path.resolve(process.cwd(), options.dir);
  
  // Check if directory exists and has files
  if (await fs.pathExists(targetDir)) {
    const files = await fs.readdir(targetDir);
    if (files.length > 0 && !options.force) {
      if (!options.yes) {
        const { confirm } = await inquirer.prompt([
          {
            type: 'confirm',
            name: 'confirm',
            message: `Directory ${options.dir} already exists and contains files. Continue?`,
            default: false
          }
        ]);
        
        if (!confirm) {
          console.log(chalk.yellow('Installation cancelled.'));
          return;
        }
      }
    }
  }

  console.log(chalk.blue(`📦 Installing ${type} components...`));
  
  // Get template registry
  const registry = await getTemplateRegistry();
  const template = registry[type];
  
  if (!template) {
    throw new Error(`Template not found for type: ${type}`);
  }

  // Create target directory
  await fs.ensureDir(targetDir);
  
  // Copy template files
  console.log(chalk.blue('📄 Copying component files...'));
  await copyTemplate(type, targetDir);
  
  // Update package.json with dependencies
  console.log(chalk.blue('📋 Updating dependencies...'));
  await updatePackageJson(template.dependencies, template.devDependencies);
  
  // Create README for the components
  await createComponentReadme(type, targetDir);
  
  console.log(chalk.green(`✅ ${type} components installed to ${options.dir}`));
  
  // Show next steps
  showNextSteps(type);
}

async function createComponentReadme(type: string, targetDir: string) {
  const readmeContent = `# FeedInbox Components

This directory contains FeedInbox ${type} components installed via the CLI.

## Usage

\`\`\`typescript
import { FeedbackWidget, NewsletterWidget, ContactWidget } from './'
import { FeedInboxSDK } from '@feedinbox/sdk'

// Initialize SDK
const feedinbox = new FeedInboxSDK({
  apiKey: 'fb_your_api_key'
})

// Use components
<FeedbackWidget 
  apiKey="fb_your_api_key"
  onSuccess={(response) => console.log(response)}
  onError={(error) => console.error(error)}
/>
\`\`\`

## Customization

These components are copied into your project, so you can:
- Modify styling and behavior
- Add your own props and features  
- Customize the UI to match your design system
- Update dependencies as needed

## Components Included

- **FeedbackWidget**: Collect user feedback with priority levels
- **NewsletterWidget**: Newsletter subscription with consent management
- **ContactWidget**: Contact form with validation

## Documentation

Visit https://feedinbox.com/docs for complete documentation.
`;

  await fs.writeFile(path.join(targetDir, 'README.md'), readmeContent);
}

function showNextSteps(type: string) {
  console.log(chalk.yellow('\n📖 Next steps:'));
  console.log('1. Install the SDK if not already installed:');
  console.log(chalk.cyan('   npm install @feedinbox/sdk'));
  
  if (type === 'react-tailwind') {
    console.log('2. Make sure Tailwind CSS is configured in your project');
    console.log('3. Import and use the components');
  } else if (type === 'react-shadcn') {
    console.log('2. Make sure shadcn/ui is configured in your project');
    console.log('3. Install required shadcn components if not already installed');
    console.log('4. Import and use the components');
  } else {
    console.log('2. Import the CSS file in your app:');
    console.log(chalk.cyan('   import "./components/feedinbox/styles.css"'));
    console.log('3. Import and use the components');
  }
  
  console.log('4. Configure your API key and start collecting feedback!');
}